import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase';

// Define callable functions
const getCreditPackagesFn = httpsCallable(functions, 'getCreditPackagesFunction');
const getPaymentLinkFn = httpsCallable(functions, 'getStripePaymentLink');
const verifyPaymentFn = httpsCallable(functions, 'verifyPayment');
const getTransactionHistoryFn = httpsCallable(functions, 'getTransactionHistory');

/**
 * Credit package available for purchase
 */
export interface CreditPackage {
  id: string;
  credits: number;
  price: number; // in cents BRL
  name: string;
  popular?: boolean;
  paymentLink?: string;
}

/**
 * Transaction record
 */
export interface Transaction {
  id: string;
  userId: string;
  type: 'purchase' | 'consumption' | 'bonus' | 'refund';
  amount: number;
  balanceBefore: number;
  balanceAfter: number;
  description: string;
  metadata?: Record<string, unknown>;
  createdAt: { _seconds: number };
}

/**
 * Get available credit packages
 */
export async function getCreditPackages(): Promise<CreditPackage[]> {
  try {
    const result = await getCreditPackagesFn();
    return result.data as CreditPackage[];
  } catch (error) {
    console.error('Error fetching credit packages:', error);
    
    // Return default packages if API fails (matching Stripe Payment Links: 1, 10, 50 credits)
    return [
      { id: 'pkg_1', credits: 1, price: 290, name: '1 Crédito' },
      { id: 'pkg_10', credits: 10, price: 1990, name: '10 Créditos', popular: true },
      { id: 'pkg_50', credits: 50, price: 7990, name: '50 Créditos' },
    ];
  }
}

/**
 * Get Stripe Payment Link URL for credit purchase
 * Opens the Stripe-hosted payment page
 */
export async function getPaymentLink(packageId: string): Promise<string> {
  try {
    const result = await getPaymentLinkFn({ packageId });
    const data = result.data as { paymentUrl: string };
    return data.paymentUrl;
  } catch (error: unknown) {
    const err = error as { code?: string; message?: string };
    
    if (err.code === 'functions/unauthenticated') {
      throw new Error('Sessão expirada. Por favor, faça login novamente.');
    }
    
    throw new Error('Erro ao obter link de pagamento. Tente novamente.');
  }
}

/**
 * Redirect to Stripe Payment Link
 * Opens the Stripe-hosted payment page in a new tab or current window
 */
export function redirectToPayment(paymentUrl: string, newTab: boolean = false): void {
  if (newTab) {
    window.open(paymentUrl, '_blank');
  } else {
    window.location.href = paymentUrl;
  }
}

/**
 * @deprecated Use getPaymentLink instead
 * Kept for backward compatibility
 */
export async function createCheckoutSession(packageId: string): Promise<string> {
  return getPaymentLink(packageId);
}

/**
 * Verify payment after redirect
 */
export async function verifyPayment(sessionId: string): Promise<{ success: boolean; credits: number }> {
  try {
    const result = await verifyPaymentFn({ sessionId });
    return result.data as { success: boolean; credits: number };
  } catch (error) {
    console.error('Error verifying payment:', error);
    throw new Error('Erro ao verificar pagamento.');
  }
}

/**
 * Get user's transaction history
 */
export async function getTransactionHistory(limit: number = 50): Promise<Transaction[]> {
  try {
    const result = await getTransactionHistoryFn({ limit });
    return result.data as Transaction[];
  } catch (error) {
    console.error('Error fetching transaction history:', error);
    return [];
  }
}

/**
 * Format price from cents to BRL string
 */
export function formatPrice(cents: number): string {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(cents / 100);
}

/**
 * Calculate price per credit
 */
export function getPricePerCredit(pkg: CreditPackage): number {
  return pkg.price / pkg.credits;
}

/**
 * Get best value package
 */
export function getBestValuePackage(packages: CreditPackage[]): CreditPackage | null {
  if (packages.length === 0) return null;
  
  return packages.reduce((best, current) => {
    const bestPerCredit = getPricePerCredit(best);
    const currentPerCredit = getPricePerCredit(current);
    return currentPerCredit < bestPerCredit ? current : best;
  });
}

